/**
 * Thermic effect of food (TEF) engine (pure functions, no I/O).
 *
 * TypeScript port of the Kotlin TEF engine, whose home is android/core/tef
 * (package com.wittdevelopment.mightypeanut.core.tef; originally MetabolicNet's tef-engine).
 * test/tef/golden/tef-golden.json is generated from the Kotlin engine and test/tef.test.ts
 * checks this port against it value for value, so keep the arithmetic in the same order.
 *
 * Every output is a low / mid / high range. Substrate coefficients: Westerterp 2004, Tappy 1996.
 * Personal modifiers carry an evidence grade; EvidenceMode decides which grades may move the
 * central estimate. Ranges always include every modifier's uncertainty.
 *
 * Double counting: TDEE formulas and observed TDEE already include TEF (about 10% of intake).
 * Show TEF as information ("usable calories"), never subtract it from intake a second time.
 */

// ---------------------------------------------------------------------
// Evidence
// ---------------------------------------------------------------------
export type EvidenceGrade = 'STRONG' | 'MODERATE' | 'LIMITED' | 'CONFLICTING';

export const EVIDENCE_GRADE_LABEL: Readonly<Record<EvidenceGrade, string>> = {
  STRONG: 'Strong',
  MODERATE: 'Moderate',
  LIMITED: 'Limited',
  CONFLICTING: 'Conflicting',
};

export const EVIDENCE_GRADE_HELP: Readonly<Record<EvidenceGrade, string>> = {
  STRONG: 'Replicated across many studies, reviews or official standards.',
  MODERATE: 'Direction supported by several studies or one large one; size uncertain.',
  LIMITED: 'One small study, or an interpolation between studied groups.',
  CONFLICTING: 'Peer-reviewed studies point in opposite directions.',
};

/**
 * STRICT: only STRONG/MODERATE modifiers move the central estimate; LIMITED/CONFLICTING only widen the range.
 * EXPLORATORY: LIMITED modifiers also move it. CONFLICTING never does, in either mode.
 */
export type EvidenceMode = 'STRICT' | 'EXPLORATORY';

export interface Citation {
  shortRef: string;
  title: string;
  doi: string | null;
  finding: string;
}

export const citationUrl = (c: Citation): string | null => (c.doi === null ? null : `https://doi.org/${c.doi}`);

export const TEF_CITATIONS = {
  WESTERTERP_2004: {
    shortRef: 'Westerterp 2004, Nutr Metab 1:5',
    title: 'Diet induced thermogenesis',
    doi: '10.1186/1743-7075-1-5',
    finding: 'Reported DIT: fat 0–3%, carbohydrate 5–10%, protein 20–30%, alcohol 10–30%. ' +
      '~10% of intake on a mixed diet. Intra-individual variability 6–30%.',
  },
  TAPPY_1996: {
    shortRef: 'Tappy 1996, Reprod Nutr Dev 36:391',
    title: 'Thermic effect of food and sympathetic nervous system activity in humans',
    doi: '10.1051/rnd:19960405',
    finding: 'Fat 0–3%, carbohydrate 5–10%, protein 20–30%. TEF reduced in obese insulin-resistant ' +
      'patients by ~50–150 kcal/day.',
  },
  FAO_2003: {
    shortRef: 'FAO Food & Nutrition Paper 77 (2003)',
    title: 'Food energy – methods of analysis and conversion factors, Table 3.3',
    doi: null,
    finding: 'Net metabolisable energy (NME): protein 13 kJ/g vs 17 ME (−24%), alcohol 26 vs 29 (−10%), ' +
      'fat and available carbohydrate unchanged; fermentable fibre 8 kJ/g, polyols 10 kJ/g.',
  },
  LIVESEY_2001: {
    shortRef: 'Livesey 2001, Br J Nutr 85:271',
    title: 'A perspective on food energy standards for nutrition labelling',
    doi: '10.1079/bjn2000253',
    finding: 'Metabolisable energy (Atwater) over-approximates usable energy by up to 25% for some foods; ' +
      'NME validated against human calorimetry.',
  },
  DU_2014: {
    shortRef: 'Du et al. 2014, Horm Metab Res 46:365',
    title: 'The thermic effect of food is reduced in older adults',
    doi: '10.1055/s-0033-1357205',
    finding: 'n=209. TEF ~1 percentage point lower in adults 60–88 vs 18–35, after controlling for meal ' +
      'size and fat-free mass. Body fat did not predict TEF.',
  },
  JONES_2004: {
    shortRef: 'Jones et al. 2004, J Clin Endocrinol Metab 89:5138',
    title: 'Role of sympathetic neural activation in age- and habitual exercise-related differences in TEF',
    doi: '10.1210/jc.2004-0101',
    finding: 'TEF ~50% greater in young vs older men and ~25% greater in exercise-trained vs sedentary men.',
  },
  MELANSON_1998: {
    shortRef: 'Melanson et al. 1998, J Gerontol A 53:B409',
    title: 'The effects of age on postprandial thermogenesis at four graded energetic challenges',
    doi: '10.1093/gerona/53a.6.b409',
    finding: 'No age effect on TEF in healthy, glucose-tolerant women.',
  },
  POEHLMAN_1988: {
    shortRef: 'Poehlman et al. 1988, Am J Clin Nutr 47:793',
    title: 'Resting metabolic rate and postprandial thermogenesis in highly trained and untrained males',
    doi: '10.1093/ajcn/47.5.793',
    finding: 'TEF LOWER in trained (55.8 kcal) than untrained (79.2 kcal) men, even after matching body composition.',
  },
  DEJONGE_BRAY_1997: {
    shortRef: 'de Jonge & Bray 1997, Obes Res 5:622',
    title: 'The thermic effect of food and obesity: a critical review',
    doi: '10.1002/j.1550-8528.1997.tb00584.x',
    finding: '22 of 29 well-controlled studies found reduced TEF in obesity; reduction tracks insulin resistance.',
  },
  FONTVIEILLE_1992: {
    shortRef: 'Fontvieille et al. 1992, Diabetologia 35:753',
    title: 'Twenty-four-hour energy expenditure in Pima Indians with type 2 diabetes',
    doi: '10.1007/BF00429096',
    finding: 'TEF 17.1% (type 2 diabetes) vs 19.8% (normal glucose tolerance) of intake.',
  },
  RAVUSSIN_1985: {
    shortRef: 'Ravussin et al. 1985, J Clin Invest 76:1268',
    title: 'Evidence that insulin resistance is responsible for the decreased thermic effect of glucose in obesity',
    doi: '10.1172/JCI112083',
    finding: 'When glucose uptake was matched, obese and lean TEF were equal — the obesity deficit is driven by insulin resistance.',
  },
  BARR_WRIGHT_2010: {
    shortRef: 'Barr & Wright 2010, Food Nutr Res 54:5144',
    title: 'Postprandial energy expenditure in whole-food and processed-food meals',
    doi: '10.3402/fnr.v54i0.5144',
    finding: 'n=17, one meal pair. Whole-food sandwich TEF 19.9% of meal energy vs 10.7% for processed.',
  },
  CALCAGNO_2019: {
    shortRef: 'Calcagno et al. 2019, J Am Coll Nutr 38:547',
    title: 'The Thermic Effect of Food: A Review',
    doi: '10.1080/07315724.2018.1552544',
    finding: "TEF rises with meal size, protein and carbohydrate; age and activity 'may' play roles.",
  },
  JUMPERTZ_2013: {
    shortRef: 'Jumpertz et al. 2013, Obesity 21:164',
    title: 'Food label accuracy of common snack foods',
    doi: '10.1002/oby.20185',
    finding: 'Measured calories a median 4.3% above label (24 US snacks), within FDA limits.',
  },
  URBAN_2010: {
    shortRef: 'Urban et al. 2010, J Am Diet Assoc 110:116',
    title: 'The accuracy of stated energy contents of reduced-energy, commercially prepared foods',
    doi: '10.1016/j.jada.2009.10.003',
    finding: 'Supermarket frozen meals averaged 8% more energy than stated; restaurant foods 18% more.',
  },
  NOVOTNY_2012: {
    shortRef: 'Novotny et al. 2012, Am J Clin Nutr 96:296',
    title: 'Discrepancy between the Atwater factor predicted and empirically measured energy values of almonds',
    doi: '10.3945/ajcn.112.035782',
    finding: 'Atwater factors OVERestimated almond energy by 32% (4.6 vs 6.0 kcal/g).',
  },
  CFR_101_9: {
    shortRef: '21 CFR 101.9',
    title: 'US FDA nutrition labeling of food',
    doi: null,
    finding: 'Calorie & macro rounding rules (c); five permitted calorie methods (c)(1)(i)(A–E); ' +
      'Class II nutrients incl. calories may not exceed 120% of the declared value (g)(5).',
  },
} as const satisfies Record<string, Citation>;

/** Every citation, in the order the Kotlin engine declares them. */
export const ALL_TEF_CITATIONS: readonly Citation[] = Object.values(TEF_CITATIONS);

// ---------------------------------------------------------------------
// Food composition
// ---------------------------------------------------------------------
/**
 * Grams of each energy-yielding component in one portion.
 *
 * US labels list TOTAL carbohydrate, which already includes fiber and sugar alcohols. Most EU
 * labels (and many Open Food Facts EU entries) list AVAILABLE carbohydrate with fiber separate.
 * Use the right constructor so fiber is never double-counted or lost.
 */
export interface FoodComposition {
  readonly proteinG: number;
  /** Digestible (available) carbohydrate: sugars + starch. Excludes fiber and polyols. */
  readonly availableCarbG: number;
  readonly fiberG: number;
  readonly sugarAlcoholG: number;
  readonly fatG: number;
  readonly alcoholG: number;
}

export class TefInputError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'TefInputError';
  }
}

function check(ok: boolean, message: string): asserts ok {
  if (!ok) throw new TefInputError(message);
}

export function foodComposition(parts: Partial<FoodComposition> = {}): FoodComposition {
  const f: FoodComposition = {
    proteinG: parts.proteinG ?? 0,
    availableCarbG: parts.availableCarbG ?? 0,
    fiberG: parts.fiberG ?? 0,
    sugarAlcoholG: parts.sugarAlcoholG ?? 0,
    fatG: parts.fatG ?? 0,
    alcoholG: parts.alcoholG ?? 0,
  };
  check(
    [f.proteinG, f.availableCarbG, f.fiberG, f.sugarAlcoholG, f.fatG, f.alcoholG].every((g) => Number.isFinite(g) && g >= 0),
    'Nutrient grams must be finite and non-negative',
  );
  return f;
}

/** From a US-style label, where total carbohydrate includes fiber and sugar alcohols. */
export function foodFromUsLabel(label: {
  proteinG: number;
  totalCarbG: number;
  fatG: number;
  fiberG?: number;
  sugarAlcoholG?: number;
  alcoholG?: number;
}): FoodComposition {
  const fiberG: number = label.fiberG ?? 0;
  const sugarAlcoholG: number = label.sugarAlcoholG ?? 0;
  // Rounded labels can print fiber > carbs (e.g. 1 g carb, 1 g fiber). Clamp, don't throw.
  const available: number = Math.max(label.totalCarbG - fiberG - sugarAlcoholG, 0);
  return foodComposition({
    proteinG: label.proteinG, availableCarbG: available, fiberG, sugarAlcoholG, fatG: label.fatG, alcoholG: label.alcoholG ?? 0,
  });
}

/** Just the three headline macros; assumes no fiber, polyols or alcohol are known. */
export function simpleFood(proteinG: number, carbG: number, fatG: number): FoodComposition {
  return foodComposition({ proteinG, availableCarbG: carbG, fatG });
}

/** Total carbohydrate as a US label would print it. */
export const totalCarbG = (f: FoodComposition): number => f.availableCarbG + f.fiberG + f.sugarAlcoholG;

export function addFoods(a: FoodComposition, b: FoodComposition): FoodComposition {
  return foodComposition({
    proteinG: a.proteinG + b.proteinG,
    availableCarbG: a.availableCarbG + b.availableCarbG,
    fiberG: a.fiberG + b.fiberG,
    sugarAlcoholG: a.sugarAlcoholG + b.sugarAlcoholG,
    fatG: a.fatG + b.fatG,
    alcoholG: a.alcoholG + b.alcoholG,
  });
}

export function scaleFood(f: FoodComposition, factor: number): FoodComposition {
  check(factor >= 0, 'Scale factor must be non-negative');
  return foodComposition({
    proteinG: f.proteinG * factor,
    availableCarbG: f.availableCarbG * factor,
    fiberG: f.fiberG * factor,
    sugarAlcoholG: f.sugarAlcoholG * factor,
    fatG: f.fatG * factor,
    alcoholG: f.alcoholG * factor,
  });
}

// ---------------------------------------------------------------------
// Energy systems
// ---------------------------------------------------------------------
const KJ_PER_KCAL = 4.184;

export type EnergySystemId = 'ATWATER_GENERAL' | 'ATWATER_FIBER_ADJUSTED' | 'NET_METABOLIZABLE';

/**
 * Ways to turn grams into kcal. None of them is "the truth"; they answer different questions.
 * 4-4-9 Atwater factors are METABOLIZABLE energy, not gross (bomb-calorimeter) energy.
 */
export interface EnergySystem {
  id: EnergySystemId;
  displayName: string;
  proteinKcalPerG: number;
  /** Applied to AVAILABLE carbohydrate. */
  carbKcalPerG: number;
  fiberKcalPerG: number;
  sugarAlcoholKcalPerG: number;
  fatKcalPerG: number;
  alcoholKcalPerG: number;
  citation: Citation;
  /** Plain-language explanation for the UI. */
  about: string;
}

export const ENERGY_SYSTEMS: Readonly<Record<EnergySystemId, EnergySystem>> = {
  ATWATER_GENERAL: {
    id: 'ATWATER_GENERAL',
    displayName: 'Atwater 4-4-9 (label method B)',
    proteinKcalPerG: 4.0, carbKcalPerG: 4.0, fiberKcalPerG: 4.0, sugarAlcoholKcalPerG: 4.0,
    fatKcalPerG: 9.0, alcoholKcalPerG: 7.0, citation: TEF_CITATIONS.CFR_101_9,
    about: 'FDA method B: 4/4/9 on total carbohydrate, so fiber and sugar alcohols count at 4 kcal/g.',
  },
  ATWATER_FIBER_ADJUSTED: {
    id: 'ATWATER_FIBER_ADJUSTED',
    displayName: 'Atwater, fiber-adjusted (label method C)',
    proteinKcalPerG: 4.0, carbKcalPerG: 4.0, fiberKcalPerG: 2.0, sugarAlcoholKcalPerG: 2.4,
    fatKcalPerG: 9.0, alcoholKcalPerG: 7.0, citation: TEF_CITATIONS.CFR_101_9,
    about: 'FDA method C: fiber at 2 kcal/g (upper-end: soluble 2, insoluble 0) and sugar alcohols at 2.4 kcal/g (xylitol / FAO generic; real range 0 to 3.0).',
  },
  NET_METABOLIZABLE: {
    id: 'NET_METABOLIZABLE',
    displayName: 'Net metabolizable energy (FAO 2003)',
    proteinKcalPerG: 4.0 * 13.0 / 17.0,
    carbKcalPerG: 4.0,
    fiberKcalPerG: 8.0 / KJ_PER_KCAL,
    sugarAlcoholKcalPerG: 10.0 / KJ_PER_KCAL,
    fatKcalPerG: 9.0,
    alcoholKcalPerG: 7.0 * 26.0 / 29.0,
    citation: TEF_CITATIONS.FAO_2003,
    about: 'FAO 2003 net metabolizable energy: protein x 13/17 and alcohol x 26/29 of the US factors, fermentable fiber 8 kJ/g, polyols 10 kJ/g. The reviewed way to say "the energy the body can actually use".',
  },
};

export const ENERGY_SYSTEM_IDS: readonly EnergySystemId[] = ['ATWATER_GENERAL', 'ATWATER_FIBER_ADJUSTED', 'NET_METABOLIZABLE'];

export interface EnergyBreakdown {
  system: EnergySystemId;
  proteinKcal: number;
  carbKcal: number;
  fiberKcal: number;
  sugarAlcoholKcal: number;
  fatKcal: number;
  alcoholKcal: number;
  totalKcal: number;
}

export function energyBreakdown(food: FoodComposition, systemId: EnergySystemId): EnergyBreakdown {
  const s: EnergySystem = ENERGY_SYSTEMS[systemId];
  const proteinKcal: number = food.proteinG * s.proteinKcalPerG;
  const carbKcal: number = food.availableCarbG * s.carbKcalPerG;
  const fiberKcal: number = food.fiberG * s.fiberKcalPerG;
  const sugarAlcoholKcal: number = food.sugarAlcoholG * s.sugarAlcoholKcalPerG;
  const fatKcal: number = food.fatG * s.fatKcalPerG;
  const alcoholKcal: number = food.alcoholG * s.alcoholKcalPerG;
  return {
    system: systemId, proteinKcal, carbKcal, fiberKcal, sugarAlcoholKcal, fatKcal, alcoholKcal,
    totalKcal: proteinKcal + carbKcal + fiberKcal + sugarAlcoholKcal + fatKcal + alcoholKcal,
  };
}

export const energyKcal = (food: FoodComposition, systemId: EnergySystemId): number => energyBreakdown(food, systemId).totalKcal;

// ---------------------------------------------------------------------
// Ranges, coefficients and modifiers
// ---------------------------------------------------------------------
/** A low / central / high estimate. Every number the engine outputs is one of these. */
export interface EstimateRange {
  readonly low: number;
  readonly mid: number;
  readonly high: number;
}

export function estimateRange(low: number, mid: number, high: number): EstimateRange {
  check(low <= mid + 1e-9 && mid <= high + 1e-9, `Expected low <= mid <= high, got ${low} / ${mid} / ${high}`);
  return { low, mid, high };
}

export const RANGE_ONE: EstimateRange = { low: 1, mid: 1, high: 1 };
export const RANGE_ZERO: EstimateRange = { low: 0, mid: 0, high: 0 };
export const exactRange = (v: number): EstimateRange => estimateRange(v, v, v);
export const multiplyRanges = (a: EstimateRange, b: EstimateRange): EstimateRange => estimateRange(a.low * b.low, a.mid * b.mid, a.high * b.high);
export function scaleRange(r: EstimateRange, k: number): EstimateRange {
  check(k >= 0, 'Range scale factor must be non-negative');
  return estimateRange(r.low * k, r.mid * k, r.high * k);
}
export const addRanges = (a: EstimateRange, b: EstimateRange): EstimateRange => estimateRange(a.low + b.low, a.mid + b.mid, a.high + b.high);

/** A substrate's thermic coefficient (fraction of that substrate's energy spent processing it). */
export interface TefCoefficient {
  range: EstimateRange;
  grade: EvidenceGrade;
  citations: readonly Citation[];
}

// Ranges verbatim from Westerterp 2004 and Tappy 1996. Mid = midpoint of the reported range.
export const TEF_COEFFICIENTS = {
  PROTEIN: { range: estimateRange(0.20, 0.25, 0.30), grade: 'STRONG', citations: [TEF_CITATIONS.WESTERTERP_2004, TEF_CITATIONS.TAPPY_1996] },
  CARBOHYDRATE: { range: estimateRange(0.05, 0.075, 0.10), grade: 'STRONG', citations: [TEF_CITATIONS.WESTERTERP_2004, TEF_CITATIONS.TAPPY_1996] },
  FAT: { range: estimateRange(0.00, 0.015, 0.03), grade: 'STRONG', citations: [TEF_CITATIONS.WESTERTERP_2004, TEF_CITATIONS.TAPPY_1996] },
  ALCOHOL: { range: estimateRange(0.10, 0.20, 0.30), grade: 'MODERATE', citations: [TEF_CITATIONS.WESTERTERP_2004] },
} as const satisfies Record<string, TefCoefficient>;

export type ModifierKind = 'age' | 'training' | 'metabolicHealth' | 'mealProcessing';

/** Common shape for every personal or meal modifier option. */
export interface TefModifier {
  kind: ModifierKind;
  /** The Kotlin enum name, e.g. SIXTY_PLUS. */
  id: string;
  displayName: string;
  factor: EstimateRange;
  grade: EvidenceGrade;
  citations: readonly Citation[];
  note: string;
}

/** Reference options (factor exactly 1, no uncertainty) are not shown as "applied". */
export const isReferenceModifier = (m: TefModifier): boolean => m.factor.low === 1 && m.factor.mid === 1 && m.factor.high === 1;

export type AgeBracketId = 'UNDER_40' | 'FROM_40_TO_59' | 'SIXTY_PLUS';
export type TrainingStatusId = 'UNTRAINED' | 'TRAINED';
export type MetabolicHealthId = 'NORMAL' | 'INSULIN_RESISTANT';
export type MealProcessingId = 'TYPICAL_OR_PROCESSED' | 'WHOLE_FOOD';

export const AGE_BRACKETS: Readonly<Record<AgeBracketId, TefModifier>> = {
  UNDER_40: {
    kind: 'age', id: 'UNDER_40', displayName: 'Under 40', factor: RANGE_ONE, grade: 'MODERATE', citations: [],
    note: 'Reference group (studies compare against adults ~18–35).',
  },
  FROM_40_TO_59: {
    kind: 'age', id: 'FROM_40_TO_59', displayName: '40–59', factor: estimateRange(0.85, 0.95, 1.00), grade: 'LIMITED',
    citations: [TEF_CITATIONS.DU_2014, TEF_CITATIONS.JONES_2004],
    note: 'No direct data for this band — linear interpolation between young and 60+ groups.',
  },
  SIXTY_PLUS: {
    kind: 'age', id: 'SIXTY_PLUS', displayName: '60+', factor: estimateRange(0.67, 0.90, 1.00), grade: 'MODERATE',
    citations: [TEF_CITATIONS.DU_2014, TEF_CITATIONS.JONES_2004, TEF_CITATIONS.MELANSON_1998],
    note: 'Largest study (n=209) found TEF ~1 point lower (≈10% relative). One small study found ~33% lower; ' +
      'another found no difference in healthy women.',
  },
};

export const TRAINING_STATUSES: Readonly<Record<TrainingStatusId, TefModifier>> = {
  UNTRAINED: { kind: 'training', id: 'UNTRAINED', displayName: 'Untrained / sedentary', factor: RANGE_ONE, grade: 'MODERATE', citations: [], note: 'Reference group.' },
  TRAINED: {
    kind: 'training', id: 'TRAINED', displayName: 'Regularly trained', factor: estimateRange(0.70, 1.00, 1.25), grade: 'CONFLICTING',
    citations: [TEF_CITATIONS.POEHLMAN_1988, TEF_CITATIONS.JONES_2004],
    note: 'Studies disagree on direction: one found ~30% LOWER TEF in endurance-trained men, another ~25% HIGHER. ' +
      'Central estimate stays at 1.0; only the range widens.',
  },
};

export const METABOLIC_HEALTH: Readonly<Record<MetabolicHealthId, TefModifier>> = {
  NORMAL: { kind: 'metabolicHealth', id: 'NORMAL', displayName: 'Normal glucose tolerance', factor: RANGE_ONE, grade: 'MODERATE', citations: [], note: 'Reference group.' },
  INSULIN_RESISTANT: {
    kind: 'metabolicHealth', id: 'INSULIN_RESISTANT', displayName: 'Insulin resistant / type 2 diabetes', factor: estimateRange(0.80, 0.86, 1.00), grade: 'MODERATE',
    citations: [TEF_CITATIONS.DEJONGE_BRAY_1997, TEF_CITATIONS.FONTVIEILLE_1992, TEF_CITATIONS.RAVUSSIN_1985, TEF_CITATIONS.TAPPY_1996],
    note: "Central value = 17.1/19.8 from Fontvieille 1992. Obesity per se isn't the driver — insulin resistance is.",
  },
};

export const MEAL_PROCESSING: Readonly<Record<MealProcessingId, TefModifier>> = {
  TYPICAL_OR_PROCESSED: {
    kind: 'mealProcessing', id: 'TYPICAL_OR_PROCESSED', displayName: 'Typical / processed', factor: RANGE_ONE, grade: 'MODERATE', citations: [],
    note: 'Reference. The processed meal in Barr & Wright (10.7%) matched the ~10% typical mixed-diet TEF.',
  },
  WHOLE_FOOD: {
    kind: 'mealProcessing', id: 'WHOLE_FOOD', displayName: 'Mostly whole, minimally processed', factor: estimateRange(1.00, 1.43, 1.86), grade: 'LIMITED',
    citations: [TEF_CITATIONS.BARR_WRIGHT_2010],
    note: 'Single study, n=17, one pair of sandwiches (1.86× TEF). Central value shrunk halfway toward no effect.',
  },
};

/** Who is eating. Deliberately has no biological-sex field: nothing peer-reviewed supports one. */
export interface TefProfile {
  age: AgeBracketId;
  training: TrainingStatusId;
  metabolicHealth: MetabolicHealthId;
}

export const DEFAULT_TEF_PROFILE: TefProfile = { age: 'UNDER_40', training: 'UNTRAINED', metabolicHealth: 'NORMAL' };

/** Age in years to the engine's age bracket. */
export function ageBracketFor(ageYears: number | null | undefined): AgeBracketId {
  if (ageYears === null || ageYears === undefined || !Number.isFinite(ageYears) || ageYears < 40) return 'UNDER_40';
  return ageYears < 60 ? 'FROM_40_TO_59' : 'SIXTY_PLUS';
}

// ---------------------------------------------------------------------
// Calculator
// ---------------------------------------------------------------------
export interface AppliedModifier {
  modifier: TefModifier;
  /** True if this modifier moved the central estimate (depends on grade and mode). */
  affectsCentralEstimate: boolean;
}

export interface TefResult {
  food: FoodComposition;
  mode: EvidenceMode;
  /** Intake on the chosen label basis (what a normal tracker would log). */
  intake: EnergyBreakdown;
  intakeKcal: number;
  /** FAO net-metabolizable energy for the same food: the official "usable energy" cross-check. */
  netMetabolizableKcal: number;
  /** Obligatory TEF from macronutrient coefficients alone, before any personal factor. */
  baselineTefKcal: EstimateRange;
  /** Baseline x modifiers. */
  personalTefKcal: EstimateRange;
  /** intake - personal TEF. Low net pairs with HIGH TEF. */
  netKcal: EstimateRange;
  /** Non-reference modifiers only. */
  modifiers: AppliedModifier[];
  /** Personal TEF as % of intake (central estimate). */
  tefPercent: number;
}

export interface TefOptions {
  profile?: TefProfile;
  mealProcessing?: MealProcessingId;
  mode?: EvidenceMode;
  /** Default is FDA method C, because TEF literature expresses TEF as a % of metabolizable energy ingested. */
  intakeSystem?: EnergySystemId;
}

export function affectsCentral(grade: EvidenceGrade, mode: EvidenceMode): boolean {
  switch (grade) {
    case 'STRONG':
    case 'MODERATE':
      return true;
    case 'LIMITED':
      return mode === 'EXPLORATORY';
    case 'CONFLICTING':
      return false;
  }
}

export function calculateTef(food: FoodComposition, options: TefOptions = {}): TefResult {
  const profile: TefProfile = options.profile ?? DEFAULT_TEF_PROFILE;
  const mealProcessing: MealProcessingId = options.mealProcessing ?? 'TYPICAL_OR_PROCESSED';
  const mode: EvidenceMode = options.mode ?? 'STRICT';
  const intakeSystem: EnergySystemId = options.intakeSystem ?? 'ATWATER_FIBER_ADJUSTED';
  const intake: EnergyBreakdown = energyBreakdown(food, intakeSystem);

  // TEF applies to the Atwater energy of each substrate. Fiber and polyols get no TEF term:
  // their energy is already discounted and there are no substrate-specific TEF data for them.
  const baseline: EstimateRange = addRanges(
    addRanges(
      addRanges(
        scaleRange(TEF_COEFFICIENTS.PROTEIN.range, food.proteinG * 4.0),
        scaleRange(TEF_COEFFICIENTS.CARBOHYDRATE.range, food.availableCarbG * 4.0),
      ),
      scaleRange(TEF_COEFFICIENTS.FAT.range, food.fatG * 9.0),
    ),
    scaleRange(TEF_COEFFICIENTS.ALCOHOL.range, food.alcoholG * 7.0),
  );

  const all: TefModifier[] = [
    AGE_BRACKETS[profile.age], TRAINING_STATUSES[profile.training], METABOLIC_HEALTH[profile.metabolicHealth], MEAL_PROCESSING[mealProcessing],
  ];
  const applied: AppliedModifier[] = all.map((modifier) => ({ modifier, affectsCentralEstimate: affectsCentral(modifier.grade, mode) }));

  // Range bounds always include every modifier's uncertainty; only the central estimate is gated by grade.
  let combined: EstimateRange = RANGE_ONE;
  for (const a of applied) {
    const f: EstimateRange = a.modifier.factor;
    combined = multiplyRanges(combined, estimateRange(f.low, a.affectsCentralEstimate ? f.mid : 1.0, f.high));
  }

  const personal: EstimateRange = multiplyRanges(baseline, combined);
  const total: number = intake.totalKcal;
  const net: EstimateRange = estimateRange(
    Math.max(total - personal.high, 0),
    Math.max(total - personal.mid, 0),
    Math.max(total - personal.low, 0),
  );

  return {
    food,
    mode,
    intake,
    intakeKcal: total,
    netMetabolizableKcal: energyKcal(food, 'NET_METABOLIZABLE'),
    baselineTefKcal: baseline,
    personalTefKcal: personal,
    netKcal: net,
    modifiers: applied.filter((a) => !isReferenceModifier(a.modifier)),
    tefPercent: total > 0 ? personal.mid / total * 100.0 : 0,
  };
}
